import WandbLoader from '@wandb/weave/common/components/WandbLoader';
import {Alert} from '@wandb/weave/components/Alert';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  opArtifactProject,
  opArtifactVersionArtifactSequence,
  opArtifactVersionCreatedBy,
  opArtifactVersionMetadata,
  opArtifactVersionRunHistoryRow,
  opDict,
  opIsNone,
  opRunConfig,
  OutputNode,
} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import * as React from 'react';
import {useMemo} from 'react';

import CollapsibleJSONTable from '../../CollapsibleJSONTable';

const inputType = 'artifactVersion' as const;
type ConfigType = {};

export const PanelArtifactVersionMetadata: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType>
> = ({input: artifactVersionNode}) => {
  // With org-level registries allowing access to team-level artifacts,
  // there are cases where users can have access to the artifact,
  // but not its source run or project/team. This access check on the frontend
  // will prevent us from making queries that we know will crash for such users.
  const noProjectAccess = useNodeValue(
    useMemo(
      () =>
        opIsNone({
          val: opArtifactProject({
            artifact: opArtifactVersionArtifactSequence({
              artifactVersion: artifactVersionNode,
            }),
          }),
        }),
      [artifactVersionNode]
    )
  );
  const {loading, result} = useNodeValue(
    React.useMemo(() => {
      if (noProjectAccess.loading || noProjectAccess.result) {
        return opDict({
          'Version Metadata': opArtifactVersionMetadata({
            artifactVersion: artifactVersionNode,
          }),
        } as any) as unknown as OutputNode<'any'>;
      }
      return opDict({
        'Run Config': opRunConfig({
          run: opArtifactVersionCreatedBy({
            artifactVersion: artifactVersionNode,
          }),
        }),
        'Run History at Log Step': opArtifactVersionRunHistoryRow({
          artifactVersion: artifactVersionNode,
        }),
        'Version Metadata': opArtifactVersionMetadata({
          artifactVersion: artifactVersionNode,
        }),
      } as any) as unknown as OutputNode<'any'>;
    }, [artifactVersionNode, noProjectAccess.loading, noProjectAccess.result])
  );
  const data = React.useMemo(() => {
    const res = loading || noProjectAccess.loading ? {} : result;
    for (const key of Object.keys(res)) {
      if (
        res[key] == null ||
        (typeof res[key] === 'object' && Object.keys(res[key]).length === 0)
      ) {
        delete res[key];
      }
    }
    return res;
  }, [loading, result, noProjectAccess.loading]);

  if (loading || noProjectAccess.loading) {
    return (
      <WandbLoader
        name="artifact-version-metadata-tab"
        active
        inline
        size="small"
      />
    );
  }
  return (
    <>
      {noProjectAccess.result && (
        <Alert severity="warning" style={{marginBottom: '8px'}}>
          You don't have access to view the history metrics for this artifact.
        </Alert>
      )}
      {!noProjectAccess.result &&
        data != null &&
        Object.keys(data).length === 0 && (
          <Alert severity="info" style={{marginBottom: '8px'}}>
            No metadata or history metrics were logged for this artifact.
          </Alert>
        )}
      {noProjectAccess.result &&
        data != null &&
        Object.keys(data).length === 0 && (
          <Alert severity="info" style={{marginBottom: '8px'}}>
            No metadata was logged for this artifact.
          </Alert>
        )}
      {data != null && Object.keys(data).length > 0 && (
        <CollapsibleJSONTable data={data} />
      )}
    </>
  );
};
